import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Sparkline } from '../Sparkline'
import { StatsApi, type CurrencyAmount } from '@/services/stats/statsApi'
import { readStatsResult } from '@/services/stats/statsQueryResult'
import { getContext } from '@/services/core/context'
import { AuthService } from '@/services/core/auth'
import { formatCurrencyAmount } from '@/utils/money'
import { formatDateTime } from '@/utils/format'
import type { BidUpdate, SiteBidHistory } from '@/services/lineItems/lineItemDiagnosticsApi'

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

export interface BidHistoryPanelProps {
  lineItemId: string
  history: SiteBidHistory[]
  isLoading: boolean
  isError?: boolean
  /** Whether the tab is showing, so spend is not fetched until it is. */
  active: boolean
}

/** The bid range of one update, "kr 10,00 – kr 14,00". */
export function bidRange(update: BidUpdate | undefined): string {
  if (!update) return '—'
  return `${formatCurrencyAmount(update.lowerCpm)} – ${formatCurrencyAmount(update.upperCpm)}`
}

function formatStamp(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : formatDateTime(d, { dateStyle: 'short', timeStyle: 'medium' })
}

/** What the line item spent per site over the last day, keyed by site id. */
async function fetchSiteSpend(lineItemId: string, startDate: string, endDate: string) {
  const response = await StatsApi.get({ lineItemId, startDate, endDate, groupBy: 'SITE' })
  const result = readStatsResult(response, 'SITE', null)
  const spend = new Map<string, CurrencyAmount>()
  for (const row of result.rows) {
    const cost = (row.stats.cost ?? row.stats.totalCost) as CurrencyAmount | undefined
    if (row.objectId && cost) spend.set(row.objectId, cost)
  }
  return spend
}

function SiteBids({ entry, spend }: { entry: SiteBidHistory; spend?: CurrencyAmount }) {
  const { t } = useTranslation()
  const latest = entry.updates.at(-1)
  const upper = entry.updates.map((u) => u.upperCpm?.amount ?? 0)

  return (
    <Collapsible className="border-b py-3 last:border-b-0">
      <div className="flex items-baseline gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">
            {entry.site.name || t('lineItemDiagnostics.unnamedSite')}
          </div>
          <div className="text-xs text-(--ad-text-muted)">
            {t('lineItemDiagnostics.spendLastDay', { amount: formatCurrencyAmount(spend, 2) })}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium tabular-nums">{bidRange(latest)}</div>
          <div className="text-xs text-(--ad-text-muted)">{t('lineItemDiagnostics.latestBid')}</div>
        </div>
      </div>
      {upper.length > 1 && (
        <Sparkline data={upper} color="var(--ad-color-1)" height={40} className="mt-2" />
      )}
      <CollapsibleTrigger className="group mt-2 flex items-center gap-1 text-xs text-(--ad-link)">
        <ChevronRight className="size-3.5 transition-transform group-data-[state=open]:rotate-90" />
        {t('lineItemDiagnostics.showUpdates', { count: entry.updates.length })}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Table className="mt-2">
          <TableHeader>
            <TableRow>
              <TableHead>{t('lineItemDiagnostics.time')}</TableHead>
              <TableHead className="text-right">{t('lineItemDiagnostics.lowerCpm')}</TableHead>
              <TableHead className="text-right">{t('lineItemDiagnostics.upperCpm')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...entry.updates].reverse().map((u, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div>{formatStamp(u.time)}</div>
                  {u.adUnit?.name && (
                    <div className="text-xs text-(--ad-text-muted)">{u.adUnit.name}</div>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCurrencyAmount(u.lowerCpm)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCurrencyAmount(u.upperCpm)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * The bid ranges dynamic bidding has set on each site, busiest site first.
 * The upper bound is drawn over time, with every update listed underneath.
 */
export function BidHistoryPanel({
  lineItemId,
  history,
  isLoading,
  isError,
  active,
}: BidHistoryPanelProps) {
  const { t } = useTranslation()

  // The last 24 hours ending on the hour, so the query key stays put between renders.
  const period = useMemo(() => {
    const end = Math.floor(Date.now() / HOUR_MS) * HOUR_MS
    return { startDate: new Date(end - DAY_MS).toISOString(), endDate: new Date(end).toISOString() }
  }, [])

  const spend = useQuery({
    queryKey: ['stats', 'bidHistorySpend', getContext(), lineItemId, period],
    queryFn: () => fetchSiteSpend(lineItemId, period.startDate, period.endDate),
    // Only this panel shows spend, so it waits until the tab is opened.
    enabled: active && AuthService.isAuthenticated() && !!lineItemId,
  })

  const sorted = useMemo(() => {
    const amount = (siteId: string) => spend.data?.get(siteId)?.amount ?? 0
    return [...history].sort((a, b) => amount(b.site.id) - amount(a.site.id))
  }, [history, spend.data])

  return (
        <div className="max-h-[28rem] overflow-y-auto">
          {isLoading && (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}
          {!isLoading && isError && (
            <p className="text-sm text-destructive">{t('lineItemDiagnostics.loadFailed')}</p>
          )}
          {!isLoading && !isError && history.length === 0 && (
            <p className="text-sm text-(--ad-text-muted)">{t('lineItemDiagnostics.noBids')}</p>
          )}
          {!isLoading && !isError && history.length > 0 && (
            <>
              <p className="mb-1 text-sm text-(--ad-text-muted)">
                {t('lineItemDiagnostics.bidHistoryDescription')}
              </p>
              {sorted.map((entry) => (
                <SiteBids key={entry.site.id} entry={entry} spend={spend.data?.get(entry.site.id)} />
              ))}
            </>
          )}
        </div>
  )
}
